import React, { useContext } from "react";
import { connect } from "react-redux";
import {
  Grid,
  Button,
  Typography,
  Card,
  CardContent,
  Box,
} from "@material-ui/core";
import CancelIcon from "@material-ui/icons/Cancel";
import intl from "react-intl-universal";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { useMutate } from "restful-react";
import { useSnackbar } from "notistack";
import { useHistory } from "react-router-dom";

import { UserContext, IUserContext } from "context/user";
import { routing } from "routing";
import { Heading } from "components/Heading";
import { TextField } from "components/TextField";
import { startLoadingAction, stopLoadingAction } from "components/ReduxSpinner";
import { createTeam, ICreateTeamInput, ICreateTeamResponse } from "apis";

import {
  FIELD_NAME,
  FIELD_ADDRESS,
  FIELD_DESCRIPTION,
  FIELD_AVATAR,
  FIELD_PLAYERS,
  FIELD_NAME_LENGTH,
  FIELD_DESCRIPTION_LENGTH,
  FIELD_ADDRESS_LENGTH,
} from "./constants";
import { CreateTeamCompProps } from "./types";

const { Urls } = routing;

const CreateTeamComp = ({ startLoading, stopLoading }: CreateTeamCompProps) => {
  const { mutate } = useMutate({
    verb: "POST",
    path: createTeam(),
  });
  const { enqueueSnackbar } = useSnackbar();

  const history = useHistory();

  const { teams, setTeams, setDefaultTeam }: IUserContext = useContext(
    UserContext
  );

  const handleCancel = () => {
    history.push(Urls.TEAM_MANAGEMENT());
  };

  return (
    <>
      <Heading
        title={intl.get("TEAM_MANAGE_ADD")}
        rightAction={
          <Button
            variant="outlined"
            startIcon={<CancelIcon />}
            onClick={handleCancel}
          >
            {intl.get("FORM_GENERAL_CANCEL")}
          </Button>
        }
      />
      <Grid container>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Formik
                initialValues={{
                  [FIELD_NAME]: "",
                  [FIELD_ADDRESS]: "",
                  [FIELD_DESCRIPTION]: "",
                  [FIELD_PLAYERS]: "",
                  [FIELD_AVATAR]: null,
                }}
                onSubmit={async (values: any) => {
                  startLoading();

                  const input: ICreateTeamInput = {
                    name: values[FIELD_NAME].trim(),
                    address: values[FIELD_ADDRESS].trim(),
                    description: values[FIELD_DESCRIPTION].trim(),
                    invitingAccounts: values[FIELD_PLAYERS].trim(),
                  };
                  mutate(input)
                    .then((response: ICreateTeamResponse) => {
                      if (response.errCode > 0) {
                        enqueueSnackbar(response.errMessage, {
                          variant: "error",
                        });
                        return;
                      }

                      if (response.teamInviteFailedAccount) {
                        enqueueSnackbar(
                          intl.get(
                            "TEAM_CREATE_SUCCESS_ACCOUNT_INVITE_FAILED",
                            {
                              accounts: JSON.stringify(
                                response.teamInviteFailedAccount
                              ),
                            }
                          ),
                          {
                            variant: "info",
                            autoHideDuration: 10000,
                          }
                        );
                      } else {
                        enqueueSnackbar(intl.get("TEAM_CREATE_SUCCESS"), {
                          variant: "success",
                        });
                      }

                      setDefaultTeam(response.team.id);
                      setTeams([...teams, response.team]);

                      history.push(Urls.TEAM_EDIT(response.team.id));
                    })
                    .catch(() => {
                      enqueueSnackbar(intl.get("TEAM_CREATE_FAILED"), {
                        variant: "error",
                      });
                    })
                    .finally(() => stopLoading());
                }}
                validationSchema={Yup.object().shape({
                  [FIELD_NAME]: Yup.string()
                    .max(
                      FIELD_NAME_LENGTH,
                      intl.get("FORM_VALIDATION_LENGTH", {
                        length: FIELD_NAME_LENGTH,
                      })
                    )
                    .required(intl.get("FORM_VALIDATION_REQUIRED")),
                  [FIELD_DESCRIPTION]: Yup.string().max(
                    FIELD_DESCRIPTION_LENGTH,
                    intl.get("FORM_VALIDATION_LENGTH", {
                      length: FIELD_DESCRIPTION_LENGTH,
                    })
                  ),
                  [FIELD_ADDRESS]: Yup.string().max(
                    FIELD_ADDRESS_LENGTH,
                    intl.get("FORM_VALIDATION_LENGTH", {
                      length: FIELD_ADDRESS_LENGTH,
                    })
                  ),
                })}
              >
                {() => {
                  return (
                    <Form>
                      <Grid container>
                        <Grid item xs={12} md={6}>
                          <Typography>
                            <strong>{`${intl.get("TEAM_EDIT_NAME")} *`}</strong>
                          </Typography>
                          <Field component={TextField} name={FIELD_NAME} />
                          <Box mt={2} />
                        </Grid>
                      </Grid>
                      <Grid container>
                        <Grid item xs={12} md={6}>
                          <Typography gutterBottom>
                            <strong>{intl.get("TEAM_EDIT_PLAYERS")}</strong>
                          </Typography>
                          <Typography gutterBottom variant="caption">
                            {`(${intl.get("TEAM_EDIT_PLAYERS_TIPS")})`}
                          </Typography>
                          <Field
                            multiline
                            rows="4"
                            component={TextField}
                            name={FIELD_PLAYERS}
                          />
                          <Box mt={2} />
                        </Grid>
                      </Grid>
                      <Grid container>
                        <Grid item xs={12} md={6}>
                          <Typography gutterBottom>
                            <strong>{intl.get("TEAM_EDIT_LOCATION")}</strong>
                          </Typography>
                          <Field component={TextField} name={FIELD_ADDRESS} />
                          <Box mt={2} />
                        </Grid>
                      </Grid>
                      <Grid container>
                        <Grid item xs={12} md={6}>
                          <Typography gutterBottom>
                            <strong>{intl.get("TEAM_EDIT_DESCRIPTION")}</strong>
                          </Typography>
                          <Field
                            component={TextField}
                            multiline
                            rows="4"
                            name={FIELD_DESCRIPTION}
                          />
                          <Box mt={2} />
                        </Grid>
                      </Grid>
                      <Button
                        type="submit"
                        variant="contained"
                        color="secondary"
                        size="large"
                      >
                        {intl.get("TEAM_SUBMIT")}
                      </Button>
                    </Form>
                  );
                }}
              </Formik>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </>
  );
};

const mapDispatch = {
  startLoading: () => startLoadingAction,
  stopLoading: () => stopLoadingAction,
};

export const CreateTeam = connect(null, mapDispatch)(CreateTeamComp);
